import React, { useState, useRef, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { UserMenu } from "./UserMenu";
import { User } from "lucide-react";

interface LayoutProps {
  children: React.ReactNode;
  fullWidth?: boolean;
}

export function Layout({ children, fullWidth = false }: LayoutProps) {
  const { user } = useAuth();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const userButtonRef = useRef<HTMLButtonElement>(null);
  const mobileButtonRef = useRef<HTMLButtonElement>(null);

  const toggleUserMenu = () => {
    setUserMenuOpen(!userMenuOpen);
  };

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node) &&
        userButtonRef.current &&
        !userButtonRef.current.contains(event.target as Node) &&
        mobileButtonRef.current &&
        !mobileButtonRef.current.contains(event.target as Node)
      ) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0d1117] flex flex-col">
      <Header toggleUserMenu={toggleUserMenu} />

      {/* Main content with user avatar sidebar and slide-out menu */}
      <div className="flex-1 flex">
        {/* User avatar sidebar - fixed width */}
        {user && (
          <div
            className={`w-20 border-r border-[#30363d] bg-[#161b22] flex flex-col items-center py-6 ${
              userMenuOpen ? "hidden" : "hidden"
            } h-full`}
          >
            <button
              ref={userButtonRef}
              onClick={toggleUserMenu}
              className="w-12 h-12 rounded-full bg-[#1f6feb]/20 text-[#58a6ff] hover:bg-[#1f6feb]/30 transition-colors flex items-center justify-center"
              aria-expanded={userMenuOpen}
              aria-haspopup="true"
            >
              <User className="h-6 w-6" />
            </button>
          </div>
        )}

        {/* Main content area */}
        <main className={`flex-1 relative ${user ? "" : ""}`}>
          <div
            className={
              fullWidth ? "w-full" : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
            }
          >
            {children}
          </div>

          {/* Mobile user avatar button - fixed at bottom left */}
          {user && (
            <button
              ref={mobileButtonRef}
              onClick={toggleUserMenu}
              className="fixed bottom-6 left-6 z-40 flex items-center justify-center w-12 h-12 rounded-full bg-[#1f6feb]/20 text-[#58a6ff] hover:bg-[#1f6feb]/30 transition-colors shadow-lg"
              aria-expanded={userMenuOpen}
              aria-haspopup="true"
            >
              <User className="h-6 w-6" />
            </button>
          )}

          {/* User menu */}
          {user && (
            <UserMenu
              user={user}
              isOpen={userMenuOpen}
              onClose={() => setUserMenuOpen(false)}
              menuRef={userMenuRef}
            />
          )}
        </main>
      </div>

      <Footer />
    </div>
  );
}
